import React, { useEffect, useState, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { MessageView } from "./messageView";
import { toast } from "react-toastify";
import VoterReachOutListing from "./voter-reachout-listing";
import VoterReachOutMessageOverview from "./voter-reachout-message-overview";
import { useStore } from "../../../helpers";
import VoterReachOutHeader from "../voter-reachout-header";
import { DeleteModal, SideOverlay } from "../../../components";
import VoterReachOutSectionHeader from "../voter-reachout-section-header";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";

interface VoterReachOutProps {
  projectid: string;
}

const VoterReachOut = observer((props: VoterReachOutProps) => {
  const { projectid } = props;
  const [popOver, setPopOver] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<string | null>(null);
  const rootStore = useStore();
  const { voterReachOutStore } = rootStore;
  const {
    formType,
    isSideOverlayOpen,
    isMessageOverviewOpen,
    selectedOverViewItem,
    messageSearchKeyword,
    setMessageSearchKeyword,
  } = voterReachOutStore;
  const sideOverlayTitle = selectedOverViewItem?.ds?.name || "Message Details";

  const getReachOutMessages = useCallback(() => {
    if (!projectid) return;

    const getMessageListPayload = {
      projectid,
      query: "",
      channel: "",
      offset: 0,
      count: 100,
    };
    voterReachOutStore.get_reachout_message(getMessageListPayload);
  }, [projectid]);

  // Load messages on mount and when the project changes
  useEffect(() => {
    getReachOutMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectid]);

  const addNewReachOut = useCallback(
    (messageType: string) => {
      voterReachOutStore.set_messageType(messageType);
      voterReachOutStore.reset_textMessageFormVal();
      voterReachOutStore.reset_voiceMessageFormVal();
      voterReachOutStore.reset_whatsappMessageFormVal();
      voterReachOutStore.set_isSideOverlay(true);
      voterReachOutStore.set_formType("new");
      setPopOver(false);
    },
    [voterReachOutStore]
  );

  const handleOverlayClose = useCallback(() => {
    voterReachOutStore.set_isSideOverlay(false);
    voterReachOutStore.set_formType("new");
  }, [voterReachOutStore]);

  const deleteHandler = useCallback(async () => {
    if (!deleteItem) return;

    try {
      const result = await voterReachOutStore.delete_reachout_message({
        id: deleteItem,
      });
      if (result.status === 200) {
        toast.success("Message deleted successfully");

        // Close overview if the deleted item was being viewed
        if (voterReachOutStore.selectedOverViewItem?.id === deleteItem) {
          voterReachOutStore.set_IsMessageOverviewOpen(false);
          voterReachOutStore.set_selected_overview_item(null);
        }

        // Manually remove the item from the list for immediate UI update
        voterReachOutStore.reachOutList = voterReachOutStore.reachOutList.filter(
          (item: any) => item.id !== deleteItem
        );

        // Force refresh by clearing the last payload cache and refetching
        voterReachOutStore.lastReachOutMessagePayload = null;
        getReachOutMessages();
      } else {
        toast.error("Failed to delete message");
      }
    } catch (error) {
      toast.error("An error occurred while deleting the message");
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteItem(null);
    }
  }, [deleteItem, voterReachOutStore, getReachOutMessages]);

  const handleMessageOverviewClose = useCallback(() => {
    voterReachOutStore.set_IsMessageOverviewOpen(false);
  }, [voterReachOutStore]);

  const handlePopOverState = useCallback((bool: boolean) => {
    setPopOver(bool);
  }, []);

  const onDeleteHandler = useCallback((itemId: string) => {
    setIsDeleteModalOpen(true);
    setDeleteItem(itemId);
  }, []);

  function useOutsideAlerter(ref: React.RefObject<HTMLElement>) {
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setPopOver(false);
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const addNewButtonRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(addNewButtonRef);

  return (
    <div>
      <div className="mt-4">
        <VoterReachOutSectionHeader
          title="Messages"
          description="Create and manage the messages you'll send to your voters."
          icon={ChatBubbleLeftRightIcon}
          action={
            <VoterReachOutHeader
              popOver={popOver}
              setPopOver={handlePopOverState}
              addNewReachOut={addNewReachOut}
              addNewButtonRef={addNewButtonRef}
            />
          }
        />
      </div>

      <div className="mt-4">
        <VoterReachOutListing
          setPopOver={handlePopOverState}
          deleteMessage={onDeleteHandler}
          search={messageSearchKeyword}
          onSearchChange={setMessageSearchKeyword}
        />
      </div>

      <SideOverlay
        show={isSideOverlayOpen}
        onClose={handleOverlayClose}
        title={formType === "new" ? "Add New Message" : "Edit Message"}
      >
        <MessageView projectId={projectid} getReachOutMessages={getReachOutMessages} />
      </SideOverlay>

      <SideOverlay show={isMessageOverviewOpen} onClose={handleMessageOverviewClose} title={sideOverlayTitle}>
        <VoterReachOutMessageOverview data={selectedOverViewItem} />
      </SideOverlay>

      {isDeleteModalOpen && (
        <DeleteModal
          isOpen={isDeleteModalOpen}
          desc="Are you sure you want to delete this message? This action cannot be undone."
          onDeleteClick={deleteHandler}
          onCancelClick={() => {
            setIsDeleteModalOpen(false);
            setDeleteItem(null);
          }}
        />
      )}
    </div>
  );
});

export default VoterReachOut;
